import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import mqttService from '../../services/mqttService';
import { useAuth } from '../../contexts/AuthContext';
import { Meter, MqttMeterImageInfo } from '../../types';

/**
 * Zählerfotos: anfordern, ansehen, herunterladen.
 *
 * Warum es das gibt: die offizielle Jahresablesung eines Zählers mit Kamera
 * verlangt sonst, dass jemand hinfährt und die Ziffern abliest. Das Gerät kann
 * ein Foto der Zählerscheibe schicken.
 *
 * Warum ein Hook und keine eigene Sektion: ein Foto gehört zu SEINEM Zähler.
 * Als eigener Abschnitt unter den Kacheln stand es weit weg von der Kachel,
 * die denselben Zähler zeigt — der Knopf sitzt jetzt im Kopf der Kachel, die
 * Fotos liegen in ihrem aufgeklappten Bereich.
 *
 * Zwei Eigenheiten, die die Bedienung prägen:
 *
 * 1. Die Antwort ist ASYNCHRON. Der Knopf legt nur ein Kommando auf den Broker;
 *    das Bild kommt Sekunden später über MQTT. Deshalb wird nach dem Klick
 *    gepollt, bis ein NEUES Bild auftaucht — nicht irgendeines, sonst meldet
 *    die Oberfläche das alte Bild als Erfolg.
 * 2. Ein Bild ohne Metadaten ist unbestätigt. `image/meta` folgt immer auf das
 *    Bild; fehlt es, kann die Übertragung abgebrochen sein. Solche Bilder
 *    werden gezeigt, aber gekennzeichnet — ein abgeschnittenes Foto als
 *    Ablesebeleg zu verwenden wäre der schlimmere Fehler.
 */

const POLL_INTERVAL_MS = 2000;
/** Wie lange auf ein neues Bild gewartet wird. ~4 s ist normal, 60 s ist großzügig. */
const POLL_TIMEOUT_MS = 60000;

interface MeterPhotoState {
  images: MqttMeterImageInfo[];
  readerKnown: boolean;
}

const toDate = (info: MqttMeterImageInfo): Date => {
  const raw = info.capturedAt ?? info.receivedAt;
  return new Date(raw.endsWith('Z') || raw.includes('+') ? raw : `${raw}Z`);
};

const formatWhen = (info: MqttMeterImageInfo): string => {
  const d = toDate(info);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/**
 * Dateiname fürs Herunterladen. Ein Foto ist ein Ablesebeleg — es soll im
 * Download-Ordner auch in einem Jahr noch erkennbar sein, ohne es zu öffnen.
 */
const downloadName = (info: MqttMeterImageInfo, meterLabel: string): string => {
  const d = toDate(info);
  const stamp = Number.isNaN(d.getTime())
    ? 'ohne-datum'
    : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      + `_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
  const label = meterLabel.replace(/[^\w\d-]+/g, '-').replace(/^-|-$/g, '') || 'zaehler';
  const ext = info.contentType?.split('/')[1]?.replace('jpeg', 'jpg') ?? 'jpg';
  return `Zaehlerfoto_${label}_${stamp}.${ext}`;
};

export interface MeterPhotos {
  /** Knopf für den Kopf der Kachel. `null`, wenn der Zähler keine Kamera hat. */
  actionFor: (meter?: Meter) => React.ReactNode;
  /** Fotoliste für den aufgeklappten Bereich. `null` ohne Kamera. */
  listFor: (meter?: Meter) => React.ReactNode;
  /** Die Vorschau. Einmal auf der Seite einhängen. */
  previewModal: React.ReactNode;
}

export const useMeterPhotos = (meters: Meter[]): MeterPhotos => {
  const { isAdmin } = useAuth();
  const [state, setState] = useState<Record<number, MeterPhotoState>>({});
  const [busyMeterId, setBusyMeterId] = useState<number | null>(null);
  const [preview, setPreview] = useState<
    { url: string; info: MqttMeterImageInfo; meterLabel: string } | null
  >(null);
  // Object-URLs müssen freigegeben werden, sonst bleibt jedes angesehene Bild
  // bis zum Reload im Speicher.
  const objectUrls = useRef<string[]>([]);

  useEffect(() => () => {
    objectUrls.current.forEach(URL.revokeObjectURL);
    objectUrls.current = [];
  }, []);

  const load = useCallback(async (meterId: number) => {
    try {
      const list = await mqttService.getMeterPhotos(meterId);
      setState(prev => ({
        ...prev,
        [meterId]: {
          images: list.images ?? [],
          readerKnown: list.readerKnown,
        },
      }));
      return list.images ?? [];
    } catch {
      // Ein Zähler ohne MQTT-Kanal ist der Normalfall, kein Fehler — still.
      return [];
    }
  }, []);

  useEffect(() => {
    meters.forEach(m => { void load(m.id); });
  }, [meters, load]);

  const request = async (meterId: number) => {
    const before = state[meterId]?.images?.[0]?.id ?? null;
    setBusyMeterId(meterId);
    try {
      await mqttService.requestMeterPhoto(meterId);
      toast.success('Foto angefordert — das Gerät blitzt und schickt es gleich.');

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      // Auf ein NEUES Bild warten. Ein vorhandenes älteres Bild darf nicht als
      // Antwort auf diesen Klick durchgehen.
      while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
        const images = await load(meterId);
        const newest = images[0];
        if (newest && newest.id !== before) {
          toast.success(newest.complete
            ? 'Foto eingetroffen.'
            : 'Foto eingetroffen, Metadaten fehlen noch.');
          return;
        }
      }
      toast.error('Kein Foto innerhalb einer Minute. Ist das Gerät online?');
    } catch (e: any) {
      const status = e?.response?.status;
      const message = e?.response?.data?.error;
      if (status === 409) {
        toast.error(message ?? 'Zu diesem Zähler ist keine lesende Platine bekannt.');
      } else if (status === 503) {
        toast.error('Keine Verbindung zum MQTT-Broker.');
      } else {
        toast.error(message ?? 'Foto konnte nicht angefordert werden.');
      }
    } finally {
      setBusyMeterId(null);
    }
  };

  const open = async (rowId: number, info: MqttMeterImageInfo, meterLabel: string) => {
    try {
      const url = await mqttService.getMeterPhotoUrl(rowId, info.id);
      objectUrls.current.push(url);
      setPreview({ url, info, meterLabel });
    } catch {
      toast.error('Foto konnte nicht geladen werden.');
    }
  };

  // Ein Zähler zählt als "mit Kamera", wenn Bilder vorliegen oder das Portal
  // die lesende Platine kennt. Beides beantwortet das Backend nur noch für
  // Kanäle mit der Leseart `ocr` — ein Modbus- oder IR-Zähler hängt zwar an
  // einer Platine, liefert aber nie ein Bild, und ein Knopf an seiner Kachel
  // könnte nur scheitern.
  const withCamera = (meter?: Meter): MeterPhotoState | null => {
    if (!meter) return null;
    const s = state[meter.id];
    return s && (s.readerKnown || s.images.length > 0) ? s : null;
  };

  const actionFor = (meter?: Meter): React.ReactNode => {
    const s = withCamera(meter);
    if (!s || !meter || !isAdmin()) return null;
    const busy = busyMeterId === meter.id;
    return (
      <button
        // Der Kartenkopf klappt bei Klick auf; dieser Knopf tut etwas anderes.
        onClick={e => { e.stopPropagation(); void request(meter.id); }}
        disabled={busy || !s.readerKnown}
        title={s.readerKnown
          ? 'Ein aktuelles Foto beim Lesegerät anfordern'
          : 'Keine lesende Platine bekannt — wird mit dem nächsten Messwert erfasst'}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium
          bg-black/5 dark:bg-white/5 text-slate-600 dark:text-slate-300
          border border-black/5 dark:border-white/10
          hover:bg-[#216dab] hover:text-white hover:border-transparent
          disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-black/5
          disabled:dark:hover:bg-white/5 disabled:hover:text-slate-600
          transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#216dab]"
      >
        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        {busy ? 'Warte auf Bild…' : 'Foto'}
      </button>
    );
  };

  const listFor = (meter?: Meter): React.ReactNode => {
    const s = withCamera(meter);
    if (!s || !meter) return null;
    const label = meter.name ?? meter.type;

    return (
      <div className="mt-8 border-t border-black/5 dark:border-white/5 pt-6">
        <div className="flex items-baseline gap-3 mb-3">
          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Zählerfotos</span>
          <span className="text-sm text-slate-400">
            Für die offizielle Ablesung — das Gerät fotografiert die Zählerscheibe.
          </span>
        </div>

        {s.images.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-slate-500 py-2">
            Noch kein Foto vorhanden.
          </p>
        ) : (
          <ul className="divide-y divide-black/5 dark:divide-white/5">
            {s.images.map(info => (
              <li key={info.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="flex flex-col min-w-0">
                  <span className="text-sm text-slate-600 dark:text-slate-300 tabular-nums">
                    {formatWhen(info)}
                  </span>
                  <span className="text-sm text-slate-400 truncate">
                    {Math.round(info.sizeBytes / 1024)} kB
                    {info.reason ? ` · ${info.reason}` : ''}
                    {!info.complete && ' · unbestätigt'}
                  </span>
                </div>
                <button
                  // Jedes Bild trägt seine eigene Zeile: derselbe Zähler kann
                  // zwei haben, wenn das Gerät seine Seriennummer geändert hat.
                  onClick={() => open(info.deviceRowId, info, label)}
                  className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium
                    text-[#216dab] dark:text-[#3aa6e9] hover:bg-[#216dab]/10
                    transition-colors focus:outline-none focus-visible:ring-2
                    focus-visible:ring-[#216dab]"
                >
                  Ansehen
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const previewModal = preview ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={() => setPreview(null)}
      role="dialog"
      aria-modal="true"
      aria-label="Zählerfoto"
    >
      <div className="max-w-4xl w-full space-y-3" onClick={e => e.stopPropagation()}>
        <img
          src={preview.url}
          alt={`Zählerfoto vom ${formatWhen(preview.info)}`}
          className="w-full h-auto rounded-xl shadow-2xl"
        />
        <div className="flex items-center justify-between text-sm text-white/80">
          <span className="tabular-nums">
            {formatWhen(preview.info)}
            {!preview.info.complete && ' · unbestätigt (Metadaten fehlen)'}
          </span>
          <div className="flex items-center gap-2">
            {/* Der Blob liegt bereits im Speicher — der Download kostet keinen
                zweiten Abruf und braucht keinen Token im Header. */}
            <a
              href={preview.url}
              download={downloadName(preview.info, preview.meterLabel)}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors
                focus:outline-none focus-visible:ring-2 focus-visible:ring-white
                inline-flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Herunterladen
            </a>
            <button
              onClick={() => setPreview(null)}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors
                focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return { actionFor, listFor, previewModal };
};
